/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useParams } from 'js/lib/useRouter';

import { Grid, Link, PageGridContainer, Typography2 } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';

import type { DiscoveryCollectionWithMetadata } from 'bundles/collections-carousel/types/CarouselCollection';
import { mapCollectionsForCarousel } from 'bundles/collections-carousel/utils/mappingUtils';
import CollectionModule from 'bundles/page-config-common/components/shared/CollectionModule';
import HomeSection from 'bundles/page-config-common/components/shared/HomeSection';
import { useHeaderLevel } from 'bundles/page-config-common/contexts/HeaderLevelContext';
import { useEnterpriseBrowseCollections } from 'bundles/page-config-common/contexts/enterprise/EnterpriseBrowseCollectionsContext';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';

import _t from 'i18n!nls/consumer-home';

const styles = {
  enterpriseCollectionHeader: css`
    margin-top: var(--cds-spacing-150);
  `,
  header: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
  `,
  button: css`
    padding-left: 0;
    padding-right: 0;
  `,
};

const EnterpriseBrowseCollectionModuleRenderer = () => {
  const { programSlug } = useParams();
  const { browseCollectionsLoading, browseCollections } = useEnterpriseBrowseCollections();
  const { currentLevel, nextLevel } = useHeaderLevel();

  const browseCollectionsWithMetadata = browseCollections?.map((collection) => ({
    ...collection,
    metadata: {
      isEnterprise: true,
      programSlug,
      titleComponent: nextLevel,
    },
  })) as DiscoveryCollectionWithMetadata[];

  const carouselCollections = mapCollectionsForCarousel(browseCollectionsWithMetadata);

  if (!browseCollectionsLoading && !carouselCollections?.length) {
    return null;
  }
  return (
    <HomeSection className="PageConfig_EnterpriseBrowseCollectionComponent" dataTestId="enterprise-browse-collections">
      {browseCollectionsLoading ? (
        <CollectionModule isCollectionLoading={browseCollectionsLoading} />
      ) : (
        carouselCollections?.map((browseCollection) => (
          <CollectionModule
            key={browseCollection.collectionId}
            isCollectionLoading={browseCollectionsLoading}
            collection={browseCollection}
            customHeader={
              <div css={styles.enterpriseCollectionHeader}>
                <Typography2 variant="titleSmall" component={currentLevel}>
                  {browseCollection.title}
                </Typography2>
              </div>
            }
          />
        ))
      )}
      <PageGridContainer>
        <Grid item xs={12}>
          <Typography2 component="p" variant="subtitleLarge">
            {_t('Want to see more? ')}
            <Link
              component={TrackedLink2}
              variant="quiet"
              data={{ programSlug }}
              trackingName="browse_collections_footer_view_all_arrow_link"
              href={`/programs/${programSlug}/view-all-collections/browse`}
            >
              {_t('View all recommendations from Coursera')}{' '}
              <ArrowNextIcon size="medium" style={{ verticalAlign: 'middle' }} />
            </Link>
          </Typography2>
        </Grid>
      </PageGridContainer>
    </HomeSection>
  );
};

export default EnterpriseBrowseCollectionModuleRenderer;
